import { Location } from '@angular/common';
import { Component, computed, Signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { Role } from '@app/models/account/role.model';
import { CompanyDocument } from '@app/models/documents/company-document.model';
import { DocumentAcknowledgement } from '@app/models/documents/document-acknowledgement.model';
import { EmployeeDocument } from '@app/models/documents/employee-document.model';
import { SignatureRequest } from '@app/models/documents/signature-request.model';
import { EmployeeDocumentForm } from '@app/modules/forms/documents/components';
import { AnalyticService, AuthService, FileHelperService, NotifyService } from '@app/services';

type EmployeeDocumentDialogType = 'ACKNOWLEDGE' | 'DETAILS' | 'EDIT';

@Component({
    templateUrl: './employee-document-show.template.html',
    styleUrls: ['./employee-document-show.style.scss'],
})
export class EmployeeDocumentShowView {
    @ViewChild('confirmDeleteDialog', { static: true }) confirmDeleteDialog!: ConfirmationDialogComponent;
    viewingSelf = false;
    employeeDocument: EmployeeDocument = new EmployeeDocument();
    documentAcknowledgement: DocumentAcknowledgement | null = null;
    userCanAcknowledge = false;
    canEdit = false;
    canDelete = false;
    roles: Role[] = [];
    currentlyOpenDialog?: EmployeeDocumentDialogType;

    employeeDocumentBackRoute: Signal<string> = computed(() => {
        if (this.auth.isAdmin()) {
            // Empty string triggers this.location.back()
            return '';
        } else {
            // TODO 📁global-document-folders
            //  Append the folderId to the navigation state,
            //  similar to the implementation in CompanyDocumentShowHeaderComponent.
            return '/profile/documents-v2/folders';
        }
    });

    /**
     * isIncompleteSignatureRequest informs the template if this document
     * is backed by a signature request that hasn't fully completed yet.
     */
    isIncompleteSignatureRequest = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private auth: AuthService,
        private location: Location,
        private notify: NotifyService,
        private fileHelper: FileHelperService,
        protected analyticService: AnalyticService
    ) {
        this.employeeDocument = this.route.snapshot.data.models.get('employeeDocument');
        this.viewingSelf = this.employeeDocument.employee.is(this.auth.employee);

        this.canEdit =
            (this.employeeDocument.isDirectUpload() || this.employeeDocument.isTaskUpload()) &&
            (this.auth.can(CompanyDocument.permission.edit) || this.isUserOwner());

        this.canDelete = this.auth.can(CompanyDocument.permission.edit) || this.isUserOwner();

        if (this.shouldRedirectToSigningView()) {
            this.router.navigate(['sign'], { relativeTo: this.route });
            return;
        }

        if (this.employeeDocument.isAcknowledgement()) {
            this.documentAcknowledgement = this.employeeDocument.source as DocumentAcknowledgement;
            this.userCanAcknowledge = this.employeeDocument.employeeId === this.auth.employee.id;
        }

        this.isIncompleteSignatureRequest =
            this.employeeDocument.isSignatureRequest() && !(this.employeeDocument.source as SignatureRequest).fileId;

        Role.where('company_id', this.auth.company.id)
            .all()
            .then(([roles, _]: [Role[], unknown]) => {
                this.roles = roles;
            })
            .catch((e) => this.notify.error(e));
    }

    onSave(form: EmployeeDocumentForm): void {
        if (!form.valid) {
            return;
        }

        const rolesSelected = form.getSelectedRoles();

        this.employeeDocument
            .sync<Role>('roles', rolesSelected)
            .save()
            .then((newDoc) => {
                this.employeeDocument = newDoc as EmployeeDocument;
                this.notify.success('document.documentSaved');
                this.viewingSelf
                    ? this.analyticService.trackEvent(AnalyticEvents.SelfProfileDocumentsMeatballEdited)
                    : this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileDocumentShowEdited);
            })
            .catch((e) => this.notify.error(e));
        this.closeDialog();
    }

    closeDialog(): void {
        this.currentlyOpenDialog = undefined;
    }

    onDelete(): void {
        this.confirmDeleteDialog
            .open()
            .then((yes: boolean) => {
                if (yes) {
                    this.viewingSelf
                        ? this.analyticService.trackEvent(AnalyticEvents.SelfProfileDocumentsMeatballDeleted)
                        : this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileDocumentShowDeleted);
                    this.employeeDocument.delete();
                    this.notify.success('document.documentDeleted');
                    this.location.back();
                }
            })
            .catch((err) => this.notify.error(err));
    }

    onDownload(): void {
        this.viewingSelf
            ? this.analyticService.trackEvent(AnalyticEvents.SelfProfileDocumentsMeatballDocDownloaded)
            : this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileDocumentShowDownloaded);
        this.fileHelper.save(
            this.employeeDocument.file.id,
            this.employeeDocument.name,
            this.employeeDocument.file.type ?? ''
        );
    }

    onAcknowledge(): void {
        if (this.documentAcknowledgement) {
            this.documentAcknowledgement.acknowledgedAt = new Date();
            this.documentAcknowledgement
                .param('companyDocument', this.documentAcknowledgement.companyDocumentId)
                .save()
                .then(() => this.notify.success('document.documentAcknowledged'));
        }
        this.closeDialog();
    }

    onDetails(): void {
        this.viewingSelf
            ? this.analyticService.trackEvent(AnalyticEvents.SelfProfileDocumentsMeatballOpenedDetails)
            : this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileDocumentShowOpenedDetails);
        this.currentlyOpenDialog = 'DETAILS';
    }

    private shouldRedirectToSigningView(): boolean {
        return (
            this.employeeDocument.isSignatureRequest() &&
            !this.employeeDocument.isComplete() &&
            this.employeeDocument.isOwnedBy(this.auth.employee)
        );
    }

    private isUserOwner(): boolean {
        if (this.employeeDocument.file && this.employeeDocument.file.uploadedBy) {
            return this.auth.employee.is(this.employeeDocument.file.uploadedBy);
        }

        return false;
    }
}
